import React, { useEffect, useState } from 'react'
import { Modal, Row, Col, Form } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import Button from '@components/common/Button/Button.component'
import {
  addNewTrackingTransaction,
  checkTransactionIfExists,
} from '@services/api/tracking.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { searchCarriersByName } from '@services/api/carrier.api'
import { logger } from '@utils/safeLogger'

import './AddClaimTracking.css'

const defaultFormData = {
  modeOfPayment: '',
  checkEFTNumber: '',
  insuranceCarrier: '',
  amount: '',
  issueDate: '',
  receivedOn: '',
  postedDate: '',
  checkStatus: '',
}

type Props = {
  isVisible: boolean
  closeModal: (isSuccess: boolean) => void
}

const AddClaimTracking: React.FC<Props> = ({ isVisible, closeModal }) => {
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [formData, setFormData] = useState(defaultFormData)
  const [debouncedCarrierName, setDebouncedCarrierName] = useState('')
  const [carriers, setCarriers] = useState<any[]>([])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleClose = (successNewTransaction: boolean = false): void => {
    setFormData(defaultFormData)
    closeModal(successNewTransaction)
  }

  const handleSave = async (): Promise<void> => {
    showLoading({
      container: '.add-claim-tracking-modal',
    })
    const newTrackingTransaction = {
      payment_method: formData.modeOfPayment,
      reference_number: formData.checkEFTNumber,
      payer: formData.insuranceCarrier,
      amount: formData.amount,
      authorized_date: formData.issueDate,
      date: formData.receivedOn,
      posted_date: formData.postedDate,
    }

    const transactionExists = await checkTransactionIfExists(
      'reference_number',
      formData.checkEFTNumber,
    )

    if (transactionExists?.data?.result?.exists) {
      setTimeout(() => {
        hideLoading('.add-claim-tracking-modal')
        showToast(
          createFailedToast(
            'Reference number already exists',
            'Reference number already exists. Please try again with a different reference number.',
          ),
        )
      }, 2000)
      return
    }

    const response = await addNewTrackingTransaction(newTrackingTransaction)

    if (response.statusCode === 200) {
      setTimeout(() => {
        hideLoading('.add-claim-tracking-modal')
        showToast(
          createSuccessToast(
            'Transaction added successfully',
            'Transaction has been added successfully.',
          ),
        )
        handleClose(true)
      }, 2000)
    } else {
      setTimeout(() => {
        hideLoading('.add-claim-tracking-modal')
        showToast(
          createFailedToast(
            'Transaction adding failed',
            'Transaction adding failed. Please try again later.',
          ),
        )
      }, 2000)
    }
  }

  const fetchCarriers = async (name: string): Promise<void> => {
    try {
      const response = await searchCarriersByName(name)
      if (response.statusCode === 200) {
        setCarriers(response.data?.carriers)
      } else {
        setCarriers([])
      }
    } catch (error) {
      logger.error('Error fetching carriers:', error)
      setCarriers([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedCarrierName.trim()) {
        fetchCarriers(debouncedCarrierName.trim())
      } else {
        setCarriers([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [debouncedCarrierName])

  const carrierNameHandleChange = (value: string): void => {
    setFormData({ ...formData, insuranceCarrier: value })
    setDebouncedCarrierName(value)
  }

  const handleCarrierSelect = (carrier: any): void => {
    setFormData({ ...formData, insuranceCarrier: carrier.carrier_name })
    setCarriers([])
  }

  return (
    <Modal
      show={isVisible}
      onHide={() => {
        setFormData(defaultFormData)
        handleClose()
      }}
      className="add-claim-tracking-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Add new claim tracking</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-2 gx-2">
          <Col>
            <Form.Group controlId="modeOfPayment">
              <Form.Label>Mode of payment</Form.Label>
              <Form.Select
                name="modeOfPayment"
                value={formData.modeOfPayment}
                onChange={handleInputChange}
              >
                <option value="">Select mode of payment</option>
                <option value="CHECK">CHECK</option>
                <option value="ACH">ACH</option>
                <option value="VIRTUAL_CARD">VIRTUAL_CARD</option>
                <option value="WIRE">WIRE</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="checkEFTNumber">
              <Form.Label>Check/EFT/Ref number</Form.Label>
              <InputField
                value={formData.checkEFTNumber}
                onChange={(e) =>
                  setFormData({ ...formData, checkEFTNumber: e.target.value })
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-2 gx-2">
          <Col md={6}>
            <InputField
              label="Payer/Carrier"
              value={formData.insuranceCarrier || ''}
              placeholder="Search"
              rightIcon={<i className="ti ti-search"></i>}
              onChange={(e) => carrierNameHandleChange(e.target.value)}
            />
            {carriers.length > 0 && (
              <Col md={6}>
                <ul className="carrier-list">
                  {carriers.map((carrier) => (
                    <li
                      key={carrier.carrier_id}
                      onClick={() => handleCarrierSelect(carrier)}
                      className="carrier-dropdown-item"
                    >
                      {carrier.carrier_name}
                    </li>
                  ))}
                </ul>
              </Col>
            )}
          </Col>
          <Col>
            <Form.Group controlId="amount">
              <Form.Label>Amount</Form.Label>
              <InputField
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="md"
          className="button-secondary me-1"
          onClick={() => handleClose()}
        >
          Cancel
        </Button>
        <Button size="md" className="button-primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddClaimTracking
